import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'
import useAuthStore from '@/store/useAuthStore'
import useGetBillingSubscription, {
  billingSubscriptionQueryKey,
  type BillingSubscription,
  type PlanFeature,
} from '@/services/api/billing/useGetBillingSubscription'

interface FeatureGateContextValue {
  /** lookup_key -> feature descriptor for the current team's plan catalogue */
  features: Record<string, PlanFeature>
  /** false until the billing/subscription query has resolved at least once */
  loaded: boolean
  hasActiveSubscription: boolean
  /** Pro availability — decides Coming soon vs Upgrade for limit upsells. */
  pro: BillingSubscription['pro']
}

const DEFAULT_PRO: BillingSubscription['pro'] = {
  availability: 'coming_soon',
  purchasable: false,
}

const FeatureGateContext = createContext<FeatureGateContextValue>({
  features: {},
  loaded: false,
  hasActiveSubscription: false,
  pro: DEFAULT_PRO,
})

export function FeatureGateProvider({ children }: { children: ReactNode }) {
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const { data, isSuccess } = useGetBillingSubscription({ teamId: currentTeamId })
  const queryClient = useQueryClient()

  // Defensive: an integration endpoint answering 421 code 10 means the
  // catalogue we hold is stale — refetch it so the integration UI hides.
  useEffect(() => {
    const onLocked = (event: Event) => {
      const lookupKey = (event as CustomEvent<{ lookupKey?: string }>).detail
        ?.lookupKey
      if (lookupKey?.startsWith('integrations.')) {
        queryClient.invalidateQueries({
          queryKey: billingSubscriptionQueryKey(currentTeamId),
        })
      }
    }
    window.addEventListener('feature-locked', onLocked)
    return () => window.removeEventListener('feature-locked', onLocked)
  }, [queryClient, currentTeamId])

  const value = useMemo<FeatureGateContextValue>(() => {
    const features: Record<string, PlanFeature> = {}
    for (const feature of data?.features ?? []) {
      features[feature.lookup_key] = feature
    }
    return {
      features,
      loaded: isSuccess,
      hasActiveSubscription: data?.has_active_subscription ?? false,
      pro: data?.pro ?? DEFAULT_PRO,
    }
  }, [data, isSuccess])

  return (
    <FeatureGateContext.Provider value={value}>
      {children}
    </FeatureGateContext.Provider>
  )
}

export function useFeatureGateContext() {
  return useContext(FeatureGateContext)
}
